import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { StepComponentProps } from "../../components/MultiStepGradeModal";
import { ClassroomManagementCreateRequestDto } from "@packages/api/madlenAPI.schemas";

export const TeachingApproachStep: React.FC<
  StepComponentProps<ClassroomManagementCreateRequestDto>
> = ({ data, onDataChange, themeColor }) => {
  const { t } = useTranslation();
  const [showCustomTool, setShowCustomTool] = useState(false);

  const teachingToolsOptions = [
    {
      id: "smart_board_tablets",
      emoji: "📱",
      label: t("classroomManagement.teachingTools.smartBoardTablets"),
      desc: t("classroomManagement.teachingTools.smartBoardTabletsDesc"),
    },
    {
      id: "traditional_board",
      emoji: "📋",
      label: t("classroomManagement.teachingTools.traditionalBoard"),
      desc: t("classroomManagement.teachingTools.traditionalBoardDesc"),
    },
    {
      id: "projector_laptop",
      emoji: "💻",
      label: t("classroomManagement.teachingTools.projectorLaptop"),
      desc: t("classroomManagement.teachingTools.projectorLaptopDesc"),
    },
    {
      id: "laboratory_equipment",
      emoji: "🔬",
      label: t("classroomManagement.teachingTools.laboratoryEquipment"),
      desc: t("classroomManagement.teachingTools.laboratoryEquipmentDesc"),
    },
    {
      id: "educational_games",
      emoji: "🎮",
      label: t("classroomManagement.teachingTools.educationalGames"),
      desc: t("classroomManagement.teachingTools.educationalGamesDesc"),
    },
    {
      id: "group_work_materials",
      emoji: "👥",
      label: t("classroomManagement.teachingTools.groupWorkMaterials"),
      desc: t("classroomManagement.teachingTools.groupWorkMaterialsDesc"),
    },
  ];

  const handleToolSelect = (toolId: string) => {
    onDataChange({ teachingToolsUsed: toolId });
  };

  const handleCustomToolToggle = () => {
    const currentTool = data.teachingToolsUsed;
    const hasCustomTool =
      currentTool &&
      !teachingToolsOptions.some((option) => option.id === currentTool);

    if (hasCustomTool || showCustomTool) {
      onDataChange({ teachingToolsUsed: undefined });
      setShowCustomTool(false);
    } else {
      setShowCustomTool(true);
    }
  };

  const handleCustomToolChange = (value: string) => {
    if (value.trim()) {
      onDataChange({ teachingToolsUsed: value.trim() });
    } else {
      onDataChange({ teachingToolsUsed: undefined });
    }
  };

  const isCustomToolSelected = () => {
    const currentTool = data.teachingToolsUsed;
    return (
      showCustomTool ||
      (currentTool &&
        !teachingToolsOptions.some((option) => option.id === currentTool))
    );
  };

  const getCustomToolValue = () => {
    const currentTool = data.teachingToolsUsed;
    if (
      currentTool &&
      !teachingToolsOptions.some((option) => option.id === currentTool)
    ) {
      return currentTool;
    }
    return "";
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-6">
        <h2
          className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-${themeColor}-600 to-${themeColor}-800 dark:from-${themeColor}-400 dark:to-${themeColor}-600`}
        >
          {t("classroomManagement.steps.teachingApproach.title")}
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t("classroomManagement.steps.teachingApproach.description")}
        </p>
      </div>

      {/* Teaching Tools Used */}
      <div>
        <h3
          className={`text-lg font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-4`}
        >
          {t("classroomManagement.form.teachingToolsUsed")}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          {t("classroomManagement.form.teachingToolsUsedDesc")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          {teachingToolsOptions.map((option) => (
            <div
              key={option.id}
              className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                data.teachingToolsUsed === option.id
                  ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
              onClick={() => handleToolSelect(option.id)}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                    data.teachingToolsUsed === option.id
                      ? `border-${themeColor}-500 bg-${themeColor}-500`
                      : "border-gray-300 dark:border-gray-600"
                  }`}
                >
                  {data.teachingToolsUsed === option.id && (
                    <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-lg">{option.emoji}</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {option.label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {option.desc}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {/* Custom Tool Option */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
              isCustomToolSelected()
                ? `border-${themeColor}-500 bg-${themeColor}-50 dark:bg-${themeColor}-900/20`
                : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
            }`}
            onClick={handleCustomToolToggle}
          >
            <div className="flex items-start gap-3">
              <div
                className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                  isCustomToolSelected()
                    ? `border-${themeColor}-500 bg-${themeColor}-500`
                    : "border-gray-300 dark:border-gray-600"
                }`}
              >
                {isCustomToolSelected() && (
                  <div className="w-2.5 h-2.5 rounded-full bg-white"></div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">✏️</span>
                  <span className="font-medium text-gray-900 dark:text-gray-100">
                    {t("classroomManagement.teachingTools.other")}
                  </span>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t("classroomManagement.teachingTools.otherDesc")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Custom Tool Input */}
        {isCustomToolSelected() && (
          <div className="mt-4">
            <label
              className={`block text-sm font-medium text-${themeColor}-700 dark:text-${themeColor}-300 mb-2`}
            >
              {t("classroomManagement.form.customTool")}
            </label>
            <input
              type="text"
              value={getCustomToolValue()}
              onChange={(e) => handleCustomToolChange(e.target.value)}
              className={`w-full rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4 text-base focus:ring-2 focus:ring-${themeColor}-500 focus:border-transparent dark:bg-gray-700/50 transition-all duration-200`}
              placeholder={t("classroomManagement.form.customToolPlaceholder")}
            />
          </div>
        )}
      </div>
    </div>
  );
};
